import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { BreakpointsService } from '@fc-core/services/breakpoints.service';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { CoreInputModule } from '@fc-shared/ui/input/core/core-input.module';
import { SpinnerComponent } from '@fc-shared/ui/spinner/spinner.component';
import { EmojiDataService } from './emoji-data.service';
import { EmojiRecentsService } from './emoji-recents.service';
import { Emoji, EmojiCategory, RawEmoji } from './emoji.model';
import { ScrollSpyDirective } from './scroll-spy.directive';

/** Synthetic category for the persisted "Frequently Used" section/tab. */
const RECENTS_CATEGORY = {
  id: 'recents',
  label: 'Frequently Used',
  icon: '🕘',
} as const;

/**
 * Shared grid geometry: 11 columns of 32px, gaps distributed across the row.
 * `display: grid` is set inline (not via Tailwind's `.grid`) because the repo
 * has a conflicting `.grid` component class that forces `display: flex`.
 */
const GRID_STYLE =
  'display: grid; justify-content: start; grid-template-columns: repeat(11, 32px); column-gap: calc((100% - 352px) / 10); row-gap: 12px;';

/**
 * In-house emoji picker. Renders the self-hosted dataset as a searchable,
 * category-tabbed grid styled with our own design tokens — no third-party
 * custom element or shadow DOM. Emits the chosen emoji as our `Emoji` shape so
 * every consumer keeps reading `.native` unchanged.
 */
@Component({
  selector: 'fc-emoji-picker',
  standalone: true,
  imports: [
    IconComponent,
    CoreInputModule,
    ScrollSpyDirective,
    SpinnerComponent,
  ],
  template: `
    <div class="flex h-full w-full flex-col bg-white">
      <!-- Category tabs -->
      <div
        class="relative flex items-stretch border-b border-secondary-100"
        role="tablist"
      >
        @for (section of sections(); track section.id) {
          <button
            type="button"
            role="tab"
            class="flex h-48 flex-1 items-center justify-center text-[22px] leading-none transition-opacity"
            [class.opacity-100]="activeCategory() === section.id"
            [class.opacity-60]="activeCategory() !== section.id"
            [attr.aria-selected]="activeCategory() === section.id"
            [attr.aria-label]="section.label"
            (click)="spy()?.scrollTo(section.id)"
          >
            {{ section.icon }}
          </button>
        }
        <!-- Single underline that slides between tabs (each tab is equal width). -->
        @if (sections().length) {
          <span
            class="pointer-events-none absolute -bottom-px left-0 h-[2px] bg-primary-600 transition-transform duration-200 ease-out"
            [style.width.%]="100 / sections().length"
            [style.transform]="indicatorTransform()"
          ></span>
        }
      </div>

      <!-- Search -->
      <div class="px-24 pb-16 pt-24">
        <fc-form-field>
          <fc-icon icon="icon-search-outline" fcPrefix [size]="20" />
          <input
            #searchInput
            type="search"
            fcInput
            placeholder="Search all emoji"
            aria-label="Search all emoji"
            [value]="query()"
            (input)="onSearch($event)"
          />
        </fc-form-field>
      </div>

      <!-- Body -->
      <div
        fcScrollSpy
        class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-24 pb-24"
      >
        @if (loading()) {
          <div class="flex h-full items-center justify-center pb-24">
            <fc-spinner [size]="28" />
          </div>
        } @else if (query()) {
          @if (searchResults().length) {
            <div [style]="gridStyle">
              @for (emoji of searchResults(); track emoji.emoji) {
                <button
                  type="button"
                  class="flex size-32 items-center justify-center rounded-lg text-[22px] leading-none hover:bg-secondary-100"
                  [attr.aria-label]="emoji.annotation"
                  [title]="emoji.annotation"
                  (click)="select(emoji)"
                >
                  {{ emoji.emoji }}
                </button>
              }
            </div>
          } @else {
            <p class="pt-24 text-center text-sm text-grey-300">
              No emoji found
            </p>
          }
        } @else {
          @for (section of sections(); track section.id) {
            <section [attr.data-cat]="section.id">
              <h3 class="pb-16 pt-8 text-base font-medium text-grey-900">
                {{ section.label }}
              </h3>
              <div [style]="gridStyle">
                @for (emoji of section.emojis; track emoji.emoji) {
                  <button
                    type="button"
                    class="flex size-32 items-center justify-center rounded-lg text-[22px] leading-none hover:bg-secondary-100"
                    [attr.aria-label]="emoji.annotation"
                    [title]="emoji.annotation"
                    (click)="select(emoji)"
                  >
                    {{ emoji.emoji }}
                  </button>
                }
              </div>
            </section>
          }
        }
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmojiPickerComponent {
  private readonly data = inject(EmojiDataService);
  private readonly recents = inject(EmojiRecentsService);
  private readonly breakpoints = inject(BreakpointsService);

  onEmojiSelect = output<Emoji>();

  protected readonly gridStyle = GRID_STYLE;

  /** Scroll container that tracks the active category and handles jumping. */
  protected readonly spy = viewChild(ScrollSpyDirective);

  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  readonly query = signal('');

  /** True until the dataset JSON resolves — drives the loading spinner. */
  readonly loading = computed(() => this.data.all().length === 0);

  readonly searchResults = computed(() => this.data.search(this.query()));

  constructor() {
    afterNextRender(() => {
      if (!this.breakpoints.isMobile()) {
        this.searchInput()?.nativeElement.focus();
      }
    });
  }

  /** Most-used emoji resolved back to full records (skips any unknown glyphs). */
  private readonly favorites = computed<RawEmoji[]>(() => {
    const byNative = this.data.byNative();
    return this.recents
      .recents()
      .map((native) => byNative.get(native))
      .filter((emoji): emoji is RawEmoji => !!emoji);
  });

  /** "Frequently Used" (when non-empty) followed by the dataset categories. */
  readonly sections = computed<EmojiCategory[]>(() => {
    const categories = this.data.categories();
    const favorites = this.favorites();
    return favorites.length
      ? [{ ...RECENTS_CATEGORY, emojis: favorites }, ...categories]
      : categories;
  });

  readonly activeCategory = computed(
    () => this.spy()?.active() || this.sections()[0]?.id || '',
  );

  /**
   * Underline offset: the indicator is one tab wide, so translating it by
   * `index * 100%` of its own width lands it exactly on the active tab.
   */
  readonly indicatorTransform = computed(() => {
    const index = this.sections().findIndex(
      (section) => section.id === this.activeCategory(),
    );
    return `translateX(${Math.max(index, 0) * 100}%)`;
  });

  select(emoji: RawEmoji): void {
    this.recents.add(emoji.emoji);
    this.onEmojiSelect.emit({
      native: emoji.emoji,
      unicode: emoji.emoji,
      name: emoji.annotation,
      shortcodes: emoji.shortcodes,
    });
  }

  onSearch(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }
}
